import { HStack, Button } from '@chakra-ui/react';

export interface Tab {
  id: string;
  label: string;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (tabId: string) => void;
}

/**
 * @useFor 2nd-tier page tabs sitting just below the page header,
 *   typically for switching between views of the same resource
 *   (e.g. People > {List, Map}; Reports > {Summary, Detail}).
 *
 *   For 3rd-tier sub-navigation under a header use `<TabMenu>`
 *   instead (different visual weight, different placement).
 *
 *   For toggle-style stateful pills (Active / All / Archived) use
 *   `<TableOptions>` or `<ToggleGroup>` — those signal stateful
 *   selection, not page navigation.
 */
export function Tabs({ tabs, activeTab, onChange }: TabsProps) {
  return (
    <HStack gap={2} mb={4}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <Button
            key={tab.id}
            size="sm"
            variant={isActive ? 'solid' : 'ghost'}
            bg={isActive ? 'surface.secondary' : 'transparent'}
            color={isActive ? 'text.primary' : 'text.secondary'}
            fontWeight={isActive ? 'semibold' : 'medium'}
            onClick={() => onChange(tab.id)}
            borderRadius="full"
            px={4}
            _hover={{
              bg: isActive ? 'surface.secondary' : 'surface.hover',
            }}
          >
            {tab.label}
          </Button>
        );
      })}
    </HStack>
  );
}
